import H from '@utils/helpers';
import { normalizeCountry } from '@utils/provider';
import { resolvePhoneDTO, toCanonicalPhone } from '@utils/phone';

import type { CheckoutValues } from '../types';

export function focusCheckoutField(selector: string) {
  const errorElement = document.querySelector(`[data-error="${selector}"]`) as HTMLElement | null;

  if (!errorElement) return;

  errorElement.classList.remove('error');
  errorElement.classList.add('error');
  errorElement.focus();
  errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
  (document.querySelector(`[data-error="${selector}"] input`) as HTMLElement | null)?.focus();

  setTimeout(() => errorElement.classList.remove('error'), 1000);
}

export function validateAndBuildAsaasPayload(values: CheckoutValues) {
  const validator = H.Validate;
  const parser = H.Parser;
  const requestPayload: any = {
    plan: values.plan,
    method: { type: values.method },
  };

  if (values.coupon?.id) requestPayload.coupon = values.coupon.id;

  if (values.method === 'credit') {
    const { number, expYear, expMonth, cvv, holderName } = values.card;
    const card = validator.card({ number, expYear, expMonth, cvv });

    if (card.error) {
      focusCheckoutField(card.field);
      throw new Error(card.error);
    }

    const holderNameResult = validator.fullName(holderName);
    if (holderNameResult.error) {
      focusCheckoutField('card-holder-name');
      throw new Error(holderNameResult.error);
    }

    requestPayload.card = {
      number: number.trim().replaceAll(' ', ''),
      ccv: cvv.trim().replaceAll(' ', ''),
      holderName: holderName.trim(),
      expiry: { month: expMonth.trim(), year: expYear.trim() },
    };
    requestPayload.method.installments = values.installments || 1;
  }

  const fullName = validator.fullName(values.name);
  if (fullName.error) {
    focusCheckoutField('name');
    throw new Error(fullName.error);
  }

  const email = validator.email(values.email);
  if (email.error) {
    focusCheckoutField('email');
    throw new Error(email.error);
  }

  const phone = validator.phone(values.phone);
  if (phone.error) {
    focusCheckoutField('phone');
    throw new Error(phone.error);
  }

  const cpf = validator.cpf(values.cpf);
  if (cpf.error) {
    focusCheckoutField('cpf');
    throw new Error(cpf.error);
  }

  const phoneDTO = resolvePhoneDTO(values.phone);
  requestPayload.customer = {
    name: parser.fullName(values.name),
    email: parser.email(values.email),
    // country (ISO2) segue junto para a lambda gravar as colunas
    // estruturadas de telefone internacional (phone_country etc.)
    phone: {
      code: phoneDTO.code,
      number: phoneDTO.number,
      ...(phoneDTO.country ? { country: phoneDTO.country } : {}),
    },
    cpf: parser.cpf(values.cpf),
  };

  return requestPayload;
}

export function validateStripeCustomer(values: CheckoutValues) {
  const fullName = H.Validate.fullName(values.name);
  if (fullName.error) {
    focusCheckoutField('name');
    throw new Error(fullName.error);
  }

  const email = H.Validate.email(values.email);
  if (email.error) {
    focusCheckoutField('email');
    throw new Error(email.error);
  }
}

export function getStripeBillingDetails(values: CheckoutValues) {
  const details: any = {};

  if (values.name) details.name = H.Parser.fullName(values.name);
  if (values.email) details.email = H.Parser.email(values.email);

  return details;
}

export function buildStripeIntentPayload(values: CheckoutValues, buyerCountry: string) {
  const requestPayload: any = {
    plan: values.plan,
    customer: {
      name: H.Parser.fullName(values.name),
      email: H.Parser.email(values.email),
    },
  };

  const country = normalizeCountry(buyerCountry);
  if (country) requestPayload.customer.country = country;

  const phone = toCanonicalPhone(values.phone);
  if (phone) requestPayload.customer.phone = phone;

  if (values.coupon?.id) requestPayload.coupon = values.coupon.id;

  return requestPayload;
}

